<template>
  <div>
    <LazySpinner v-if="isPending" />
    <div v-else-if="error">{{ error }}</div>
    <LazyNotFound
      v-else-if="course === null"
      type="course"
      :id="route.params.courseId"
    />

    <div v-else-if="course">
      <header class="page-width">
        <h1>
          {{ course.name }}
        </h1>

        <span class="button-group">
          <Button v-if="writeable" :href="`/courses/${course.id}/edit`"
            >Edit Course</Button
          >
          <Button @click="showImport = true">Upload run GPX</Button>
          <Button primary :href="`/courses/${course.id}/run`"
            >Run Course</Button
          ></span
        >
      </header>
      <ImportRunTrack
        v-if="showImport"
        :course-id="course.id"
        @close="showImport = false"
      />
      <main class="page-width">
        <section>
          <p>
            Created by:
            <UserList :users="[creator]" link />
          </p>
          <p>
            {{
              course.description && course.description.length > 0
                ? course.description
                : "No description provided."
            }}
          </p>
          <Chip>
            <DifficultyIcon :difficulty="course.difficulty" />
            {{ course.difficulty || "Unknown Difficulty" }}
          </Chip>
          <Chip
            ><i-material-symbols-check-circle-outline />&nbsp;
            {{ course.checkpoints.length }}</Chip
          >
          <Chip v-if="course.estimated_duration_hours">
            <i-ion-timer-outline />&nbsp;
            {{
              course.estimated_duration_hours
                ? durationTime
                : "No Duration Specified"
            }}
          </Chip>
          <p>{{ runsCount }} runs.</p>

          <br />

          <div v-if="course.print_files.length > 0">
            <p>
              The course designer has provded
              {{ course.print_files.length == 1 ? "this map" : "these maps" }}
              if you want to use
              {{ course.print_files.length == 1 ? "it" : "them" }}:
            </p>
            <div v-for="file in compact(course.print_files)" class="file">
              <a download target="_blank" :href="file.url || undefined">
                <i-material-symbols-download-rounded />
                {{ file.name }}
              </a>
            </div>
          </div>

          <ExportCourse :course="course" />

          <LazyCourseLeaderboard
            :course-id="course.id"
            @hoverCheckpoints="visitedCheckpointIds = $event"
          />
        </section>

        <section class="primary">
          <div class="map-container">
            <Map :fit="checkpoints">
              <CheckpointMarker
                v-for="cp in checkpoints"
                :key="cp.id"
                :checkpoint="cp"
                :visited="visitedCheckpointIds.includes(cp.id)"
                :start="cp.id == course.start_checkpoint_id"
                :end="cp.id == course.end_checkpoint_id"
              />
              <MapConnectingLines
                v-if="course.visit_in_order"
                :checkpoints="checkpoints"
                :end-is-start="
                  course.start_checkpoint_id == course.end_checkpoint_id
                "
              />
              <LiveLocations :course-id="route.params.courseId" />
            </Map>
          </div>
        </section>
      </main>
    </div>
  </div>
</template>
<script lang="ts" setup>
import { useQuery } from "@tanstack/vue-query";

const visitedCheckpointIds = ref<string[]>([]);
const route = useRoute();

const supabase = useClient();
const courseId = useParam("courseId");

const { isPending, ...courseQuery } = useCourseForView(courseId);
const { data: runsCount } = useQuery({
  queryKey: computed(() => ["run-count", param(route.params.courseId)]),
  queryFn: async () => {
    const { error, count } = await supabase
      .from("runs")
      .select(undefined, { count: "exact" })
      .not("started_at", "is", null)
      .eq("course_id", route.params.courseId);

    if (error) throw error;
    return count;
  },
});

const creator = computed(
  () => courseQuery.data.value?.course_users?.at(0)?.user
);

const profileId = usePublicProfileId();
const writeable = computed(() =>
  courseQuery.data.value?.course_users?.find(
    (u) => u.user_id == profileId.value && u.write
  )
);
const course = computed(() => courseQuery.data.value);
const error = computed(() => courseQuery.error.value);

const checkpoints = computed(() => compact(course.value?.checkpoints));
const showImport = ref(false);

const durationTime = computed(() => {
  if (!course.value?.estimated_duration_hours) return "unknown";
  if (course.value.estimated_duration_hours < 2)
    return `${Math.round(course.value.estimated_duration_hours * 60)}m`;
  return `${Math.round(course.value.estimated_duration_hours)}h`;
});
</script>
<style scoped>
.map-container {
  width: 100%;
  height: 500px;
  box-shadow: var(--subtle-shadow);
}

main {
  display: flex;
  flex-direction: row;
  margin-top: var(--grid);
  gap: var(--grid-2);
}

@media (max-width: 750px) {
  main {
    flex-direction: column;
  }
}

section {
  flex-grow: 1;
  flex-shrink: 1;
  flex-basis: 0;
}

header {
  width: 100%;
  display: flex;
  justify-content: space-between;
}

h1 {
  display: flex;
  align-items: center;
  gap: 0.3rem;
}

section.primary {
  flex-grow: 3;
  flex-shrink: 3;
}

p {
  margin-bottom: var(--half-grid);
}
</style>
